import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  cn,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import {
  ActivityIcon,
  AlertTriangleIcon,
  CheckCircle2Icon,
  ClockIcon,
  HistoryIcon,
  InboxIcon,
  RefreshCwIcon,
  ServerIcon,
} from "lucide-react";
import type { PackageNotificationsOpsStatus } from "../../domain/contracts/ops-status";
import { WorkerStatusBanner } from "./WorkerStatusBanner";

type PackageNotificationsOpsPanelProps = {
  data?: PackageNotificationsOpsStatus;
  isLoading?: boolean;
  isFetching?: boolean;
  onRefresh?: () => void;
};

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso.replace(" ", "T")).toLocaleString("lo-LA", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function MetricTile({
  label,
  value,
  hint,
  accentClassName,
}: {
  label: string;
  value: string;
  hint?: string;
  accentClassName?: string;
}) {
  return (
    <div
      className={cn(
        "rounded-lg border bg-muted/20 p-3",
        accentClassName,
      )}
    >
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className="mt-1 font-semibold text-lg tabular-nums">{value}</p>
      {hint ? (
        <p className="mt-1 text-muted-foreground text-xs">{hint}</p>
      ) : null}
    </div>
  );
}

function alertStyles(level: PackageNotificationsOpsStatus["alerts"][number]["level"]) {
  if (level === "error") {
    return "border-destructive/40 bg-destructive/5 text-destructive";
  }
  if (level === "warn") {
    return "border-amber-500/40 bg-amber-500/5 text-amber-800 dark:text-amber-200";
  }
  return "border-emerald-500/40 bg-emerald-500/5 text-emerald-800 dark:text-emerald-200";
}

function alertIcon(level: PackageNotificationsOpsStatus["alerts"][number]["level"]) {
  if (level === "ok") {
    return <CheckCircle2Icon className="size-4 shrink-0" />;
  }
  return <AlertTriangleIcon className="size-4 shrink-0" />;
}

export function PackageNotificationsOpsPanel({
  data,
  isLoading,
  isFetching,
  onRefresh,
}: PackageNotificationsOpsPanelProps) {
  const nav = useNavigate();

  return (
    <Card className="gap-0">
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0 pb-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <ActivityIcon className="size-5 text-primary" />
            <CardTitle>ສະຖານະລະບົບ (Ops)</CardTitle>
            {data ? (
              <WorkerStatusBanner health={data.worker} embeddedOnSettingsPage />
            ) : null}
          </div>
          <CardDescription className="mt-1">
            Worker, scheduler, ຄິວ ແລະອັດຕາສຳເລັດ 24h — ອັບເດດອັດຕະໂນມັດ
          </CardDescription>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0"
          disabled={isFetching}
          onClick={onRefresh}
        >
          <RefreshCwIcon
            className={cn("mr-1 size-4", isFetching && "animate-spin")}
          />
          Refresh
        </Button>
      </CardHeader>

      <CardContent className="space-y-4 pt-0">
        {isLoading ? (
          <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດສະຖານະ...</p>
        ) : data ? (
          <>
            <div className="space-y-2">
              {data.alerts.map((alert) => (
                <div
                  key={alert.key}
                  className={cn(
                    "flex items-start gap-2 rounded-lg border px-3 py-2 text-sm",
                    alertStyles(alert.level),
                  )}
                >
                  {alertIcon(alert.level)}
                  <span>{alert.message}</span>
                </div>
              ))}
            </div>

            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <MetricTile
                label="Worker"
                value={
                  data.worker.connected
                    ? "Connected"
                    : data.worker.needsQr
                      ? "Needs QR"
                      : data.worker.status
                }
                hint={
                  data.worker.messagesToday != null
                    ? `ສົ່ງມື້ນີ້ ${data.worker.messagesToday} (Worker)`
                    : data.worker.lastConnectedAt
                      ? `ເຊື່ອມລ່າສຸດ ${formatDateTime(data.worker.lastConnectedAt)}`
                      : undefined
                }
                accentClassName="border-emerald-500/20"
              />
              <MetricTile
                label="Scheduler"
                value={`${data.scheduler.sentToday}/${data.scheduler.maxToday}`}
                hint={`${data.scheduler.autoEnabled ? "Auto ເປີດ" : "Auto ປິດ"} · ${data.scheduler.cronDescription}${data.scheduler.isWarmup ? " · warm-up" : ""}`}
                accentClassName="border-blue-500/20"
              />
              <MetricTile
                label="ຄິວ pending"
                value={String(data.queue.pending)}
                hint={
                  data.queue.oldestPendingMinutes != null && data.queue.pending > 0
                    ? `ຄ້າງນานສຸດ ~${data.queue.oldestPendingMinutes} ນາທີ`
                    : data.queue.failed > 0
                      ? `failed ${data.queue.failed} ລາຍການ`
                      : "ບໍ່ມີຄິວຄ້າງ"
                }
                accentClassName={
                  data.queue.stalePending
                    ? "border-amber-500/30 bg-amber-500/5"
                    : undefined
                }
              />
              <MetricTile
                label="Logs 24h"
                value={
                  data.logs24h.total > 0
                    ? `${data.logs24h.sent} sent · ${data.logs24h.failed} fail`
                    : "0"
                }
                hint={
                  data.logs24h.failedRatePercent != null
                    ? `ອັດຕາລົ້ມເຫຼວ ${data.logs24h.failedRatePercent}%`
                    : "ຍັງບໍ່ມີການສົ່ງ 24h"
                }
                accentClassName={
                  data.logs24h.highFailedRate
                    ? "border-destructive/30 bg-destructive/5"
                    : undefined
                }
              />
            </div>

            <div className="grid gap-3 text-sm sm:grid-cols-2">
              <div className="rounded-lg border bg-muted/10 p-3">
                <p className="flex items-center gap-2 font-medium">
                  <ServerIcon className="size-4 text-primary" />
                  Scheduler & monitoring
                </p>
                <ul className="mt-2 space-y-1 text-muted-foreground">
                  <li>
                    ຊ່ວງສົ່ງ auto: {data.scheduler.sendWindowStart} –{" "}
                    {data.scheduler.sendWindowEnd} ({data.scheduler.timezone})
                  </li>
                  <li>ໂຄວຕ້າເຫຼືອ: {data.scheduler.remainingQuota} ຂໍ້ຄວາມ</li>
                  <li>
                    Auto run ລ່າສຸດ:{" "}
                    {formatDateTime(data.scheduler.lastAutoRunAt)}
                  </li>
                  <li>{data.monitoring.healthPollDescription}</li>
                </ul>
              </div>
              <div className="rounded-lg border bg-muted/10 p-3">
                <p className="font-medium">ລິ້ງດ່ວນ</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => nav({ to: "/app/package-notifications" })}
                  >
                    <InboxIcon className="mr-1 size-4" />
                    ແຈ້ງເຕືອນແພັກ
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => nav({ to: "/app/package-notifications/logs" })}
                  >
                    <HistoryIcon className="mr-1 size-4" />
                    Logs / ຄິວ
                  </Button>
                </div>
                <p className="mt-3 text-muted-foreground text-xs leading-relaxed">
                  Session ຫຼຸດ → Scan QR ດ້ານລຸ່ມ · Worker down → ກວດ{" "}
                  <code className="rounded bg-muted px-1">fanglao-wa-worker</code>{" "}
                  · ຄິວຄ້าง → Process queue ຫลัง Worker connected
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3 text-muted-foreground text-xs">
              <span className="flex items-center gap-1">
                <ClockIcon className="size-3.5" />
                ກວດຄັ້ງລ່າສຸດ: {formatDateTime(data.checkedAt)}
              </span>
              <Badge variant="outline" className="font-normal">
                Health poll {data.monitoring.healthPollCron}
              </Badge>
            </div>
          </>
        ) : (
          <p className="text-muted-foreground text-sm">ບໍ່ມີຂໍ້ມູນສະຖານະ</p>
        )}
      </CardContent>
    </Card>
  );
}
